import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { Badge } from '@abyss/web/ui/Badge';
import { Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { shouldShowDocASAP } from '../../frontends/ProviderSearch/routes/MapView/utils';
import { useCurrentPcpBadge } from '../../hooks/useCurrentPcpBadge';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { useSpecialtiesResult } from '../../hooks/useSpecialties';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useSuppressNiceMembers } from '../../hooks/useSuppressNiceMembers';
import { BreadcrumbSessionStorage } from '../../models/BreadcrumbSessionStorage';
import { BhProviderIcons, CustomPreferredPolicy } from '../../models/Lagoon';
import { FacilityDDPCode, Provider } from '../../models/Provider';
import {
  Address as ProviderAddress,
  ProviderType,
  RecommendationDetails,
} from '../../models/ProviderDetails';
import { ProviderSearchFilters } from '../../models/ProviderSearch';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { useAnalyticsStore } from '../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../store/useAnalyticsStore/analyticsStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../store/useDetailsStore';
import { DetailsStore } from '../../store/useDetailsStore/detailsStore';
import { useGuestAcquisitionStore } from '../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { useVirtualCareStore } from '../../store/useVirtualCareStore';
import { getSpecialityTitle } from '../../utils/card.utils';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { getBhIcon } from '../../utils/iconography.utils';
import { clearRoute, setResultIcon } from '../../utils/map.utils';
import {
  displayPreferredFacilityOnCards,
  getPremiumCarePhysician,
  getProviderDetailsRouteUrl,
  getProviderDetailsUrl,
  getSuppressFacilityFlags,
  isShowPreferredFacilityBadge,
} from '../../utils/providerDetails.utils';
import {
  getAutoIncrementRadius,
  shouldSuppressAddressAndMiles,
} from '../../utils/providerSearch.utils';
import { getShowCost } from '../../utils/showCost.utils';
import {
  getCurrentMember,
  getCustomPreferredPolicyData,
  getIsOxfordAndUSP,
  getLoggedInMember,
  getMarketType,
  getNetworkIdsForPES,
  getProductCodeForCoverageType,
  getSitusState,
} from '../../utils/user.utils';
import { adobeSearchResultClickEvent } from '../AdobeTagging/adobeSearchResultClickEvent';
import {
  CareCategories,
  Constants,
  ReverseCoverageTypesCodes,
  ReverseDentalCodes,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import {
  CardButtonGroupedLocationStyle,
  mobileOnly,
  notXlScreenOnly,
  phoneOnly,
} from '../ConstantsStyles';
import { ImageComponent } from '../ImageComponent';
import { PSXIcon } from '../PSXIcon';
import { SmartChoiceScoreCard } from '../SnackCard/SmartChoiceScoreCard';
import { convertObjectToUrlParams, valCheck } from '../Utils';
import {
  appendCoverageTypes,
  convertProviderTypeToAdobeType,
  formatProviderId,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import { getTierProviderEligibility } from '../Utils/memberUtils/memberUtils';
import { truncateLargeStrings } from '../Utils/truncateLargeStrings';
import {
  defaultStringToEmpty,
  getAcoBenefitPlan,
} from '../Widget/SmartChoiceWidget/utils';
import { Address } from './Address';
import { ChoosePCP } from './ChoosePCP';
import { CostIndicatorPopover } from './CostIndicatorPopover';
import {
  AvatarDataCard,
  DataCardAvatarContainer,
  GroupedLocationDrawerCardContainer,
  GroupedPinSmartChoiceListViewAvatar,
  ImageFlexContainer,
  NetworkWrapper,
  ResponsiveText,
  ResponsiveTextSpeciality,
  SnackRole,
  VirtualVisitLabel,
  VirtualVisitPatientStyleGroupedLocation,
  VoiceOnlyText,
  costIndicatorStyles,
  flexSpecialityAndBadgeStyle,
  groupedLocationDrawerNameAndBadgeStyle,
  providerNameListViewToolTipStyles,
  virtualVistsToolTipStyles,
} from './DataCard.style';
import {
  displayPremiumCareIdentifier,
  displayTier1Identifier,
  displayTier2Identifier,
  getIsSmartChoicePriceCheck,
  getLinkNameForSearchResultEvent,
  getLinkPositionIndex,
  inOrOutOfNetwork,
  isIFPOrganizationCheck,
  isPremiumCareDisplayEligible,
  ratingsOnClickHandler,
  shouldDisplayVirtualVisit,
  shouldShowCursorOnRatings,
  shouldSuppressVirtualCare,
} from './DataCardUtils';
import { Miles } from './Miles';
import { NameHeading } from './NameHeading';
import { ProviderRating } from './ProviderRating';

const truncateSpecialityTitle = (
  isGroupedLocationDrawer,
  specialityTitleText,
  notXlScreen
) =>
  isGroupedLocationDrawer
    ? truncateLargeStrings(specialityTitleText, notXlScreen ? 20 : 40)
    : specialityTitleText;

type Props = {
  popupContainer?: any;
  practitioner?: Provider;
  surveyCount?: number;
  updatePin?: (practitioner?: Provider) => void;
  handleDirectionClick?: () => void;
  index?: number;
  enableCost?: boolean;
  pageNumber?: number;
  map?: any;
  selectedFilters?: string;
  setRouteEndCoords?(coords: [number | null, number | null]): void;
  usedAt: string;
  searchTerm?: string;
  setNavigateToDirections?: (a: boolean) => void;
  headers: ResponseHeaders;
  providerType?: string;
  linkLocationName: string;
  sectionTypeDerived: string;
  providerId: string;
  locationId: string;
  isTieredProvider: boolean;
  speciality?: string;
  setSearchFilters: (a: ProviderSearchFilters) => void;
  searchFilters: ProviderSearchFilters;
  setSelectedId: (a: string | null) => void;
  setHighlightId: (a: { providerId: string; from: string }) => void;
  highlightId: { providerId: string; from: string };
  showBestMatchDetails: boolean;
  setIsOpen: (a: boolean) => void;
  virtualCareIndicatorEnabled: boolean;
  cardRef: React.RefObject<HTMLElement>;
  handleHoverOnCard: (a: any) => void;
  imgRef: any;
  name: string;
  providerSpeciality: string;
  suppressCostEstimates: boolean;
  suppressPatientReviews: boolean;
  healthGradeRating?: string;
  networkStatus?: string;
  npi: string;
  distance?: string;
  address?: ProviderAddress;
  primaryPhone: string;
  choosePCP: boolean;
  docasapPracticeId: string;
  organizationCode?: string[];
  isMNR?: boolean;
  showSmartChoice?: boolean;
  isTieredPlan?: boolean;
  acoProvider?: boolean;
  showInternalCardInfo?: boolean;
  badgeCardType?: string;
  searchRadius?: number;
  expandedProviderListView?: boolean;
  enableMapViewHideOption?: boolean;
  providerList?: Provider[];
  isGroupedLocationDrawer?: boolean;
  currentPage?: number;
  drawerContent?: any;
  virtualIndicator?: string;
  networkId?: string;
  recommendationPreferences?: any;
};

export const GroupedPinDataCard = ({
  searchTerm,
  selectedFilters,
  index,
  providerType,
  popupContainer,
  linkLocationName,
  headers,
  practitioner,
  sectionTypeDerived,
  providerId,
  locationId,
  isTieredProvider,
  speciality,
  setSearchFilters,
  searchFilters,
  pageNumber,
  handleDirectionClick,
  usedAt,
  setNavigateToDirections,
  setSelectedId,
  map,
  setHighlightId,
  updatePin,
  setRouteEndCoords,
  highlightId,
  showBestMatchDetails,
  setIsOpen,
  virtualCareIndicatorEnabled,
  cardRef,
  imgRef,
  name,
  providerSpeciality,
  suppressCostEstimates,
  suppressPatientReviews,
  healthGradeRating,
  networkStatus,
  npi,
  enableCost,
  distance,
  address,
  primaryPhone,
  choosePCP,
  docasapPracticeId,
  organizationCode,
  showSmartChoice,
  isTieredPlan = false,
  acoProvider = false,
  badgeCardType = 'data-card',
  searchRadius,
  expandedProviderListView,
  enableMapViewHideOption = false,
  providerList,
  isGroupedLocationDrawer = false,
  virtualIndicator,
  currentPage,
  drawerContent,
  handleHoverOnCard,
  networkId,
  recommendationPreferences,
}: Props) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const mobileScreen = useMediaQuery(phoneOnly);
  const { portalData } = useContext(PortalContext);
  const portalShowCost = getShowCost(portalData);
  const currentMember = getCurrentMember();
  const [showDirection, setShowDirection] = useState(false);
  const [searchParams] = useSearchParams();
  const [breadcrumbSessionStorage] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );

  const addressMilesRef = useRef<HTMLDivElement>(null);

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const {
    chipValue: value,
    coverageType: coverageTypeValue,
    lob: storeLob,
    setChipValue: setStoreChipValue,
    setCoverageType: setStoreCoverageType,
  } = chipStore;

  const featureFlags = useLagoon('feature-flags')();

  const [
    isDentalLocationNameEnable,
    schedulingEnabled,
    ctaDisplayLogic,
    isPlatinumProviderIdentifierEnabled,
    isLimitedAccessToProvider,
    isSchedulingWithFacilityEnabled,
    preferredFacilityFlag,
    oxfordPlnFlag,
    suppressPremiumCare,
    enablePCPParamsFromURL,
    enableVirtualCareEpic,
    enablePlanTypeSelectionInFilter,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_DENTAL_LOCATION_NAME,
    ConstantsLagoon.FEATURE_FLAGS.ONLINE_SCHEDULING,
    ConstantsLagoon.FEATURE_FLAGS.PSX_CTA_DISPLAY_LOGIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLATINUM_PROVIDER_IDENTIFIER,
    ConstantsLagoon.FEATURE_FLAGS
      .LIMITED_ACCESS_TO_PROVIDER_BANNER_AND_SCHEDULE,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_SCHEDULING_WITH_FACILITY_LIMITED_ACCESS_PROVIDERS,
    ConstantsLagoon.FEATURE_FLAGS.PREFERRED_FACILITY_IDENTIFICATION,
    ConstantsLagoon.FEATURE_FLAGS.OXFORD_PLN,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PCP_PARAMS_FROM_URL,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLAN_TYPE_SELECTION_IN_FILTER,
  ]);

  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();

  const isPlatinumProvider = practitioner?.isPlatinumProvider;

  const isGreenDiamondIndicator = practitioner?.isGreenDiamondProvider;

  const doShowDocASAP = shouldShowDocASAP(schedulingEnabled, docasapPracticeId);

  const notXlScreen = useMediaQuery(notXlScreenOnly);
  const smallScreen = useMediaQuery(mobileOnly);

  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      sectionType: state.typeaheadSearchStore.sectionType,
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );

  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );

  const {
    choosePCP: choosePCPFlag,
    setPCPSearchState,
    dependentSeqNbr,
  } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
      setPCPSearchState: state.setPCPSearchState,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );

  const { isNavigatingMaestro } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      isNavigatingMaestro: state.isNavigatingMaestro,
    }))
  );

  const handleAdobeSearchResultClickEvent = (clickSection?: string) => {
    adobeSearchResultClickEvent({
      filters: selectedFilters,
      linkName: getLinkNameForSearchResultEvent(providerType, clickSection),
      linkPosition: getLinkPositionIndex(index),
      linkLocation: linkLocationName,
      customAttributesBlock: {
        correlationId: headers.correlationId,
        providerId: formatProviderId(
          appendCoverageTypes(practitioner, coverageType)
        ),
        providerType: convertProviderTypeToAdobeType(providerType),
      },
      term: searchTerm,
    });
  };

  const getBhIconName = (): string => {
    if (coverageType !== 'B') return '';
    return getBhIcon(
      [],
      [providerSpeciality],
      bhProviderIcons,
      bhSpecialtyCodes
    );
  };

  const coverageType = coverageTypeValue;
  const bhProviderIcons: BhProviderIcons[] = useLagoon('bh_provider_icons')();
  const { data: bhSpecialtyCodes = [] } = useSpecialtiesResult({
    coverageType: 'B',
  });
  const bhIcon = getBhIconName();

  const { showPcpBadge } = useCurrentPcpBadge({
    choosePCP: choosePCPFlag,
    coverageType,
    practitioner,
    providerId,
  });

  const pcpBadgeDisplay = () =>
    showPcpBadge ? (
      <Badge
        css={{ marginLeft: '$sm' }}
        data-testid="data-card-pcp-badge"
        outline
        variant="success"
      >
        {t('PCP')}
      </Badge>
    ) : null;
  const isNameSearch = searchParams.get('searchType') === 'name';
  const isNotSpecialitySearch =
    searchParams.get('searchBySpeciality') === 'false';
  const setLastSelectedCardSessionStorage = (key, value) => {
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.LAST_SELECTED_FROM_DRAWER,
      {
        providerId,
        currentPage,
        drawerContent,
        breadcrumbs: {
          [ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key]:
            breadcrumbSessionStorage[
              ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key
            ],
          [key]: value,
        },
      }
    );
  };
  const handleDetailsOnClick = ({ showDirection }) => {
    handleAdobeSearchResultClickEvent();
    const typeaheadStoreValues = {
      sectionType: sectionTypeDerived,
      searchTerm,
    };
    const detailsStoreValues = {
      providerId,
      locationId,
      showDirection,
      isTieredDataCard: isTieredProvider,
      specialtySearch: speciality,
      organizationCode,
    };
    setStoreChipValue(value);
    setStoreCoverageType(coverageType);

    setDetailsStore(detailsStoreValues);
    setTypeaheadSearchStore(typeaheadStoreValues);
    setSearchFilters({
      ...searchFilters,
      PageNumber: pageNumber,
    });

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_ID,
      providerId
    );

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.IS_PROVIDER_CARD_SELECTED,
      true
    );

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_SPECIALTY,
      speciality
    );

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_FACILITY_LOCATION_ID,
      locationId
    );

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.PROVIDER_LIST,
      providerList
    );
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_INDEX,
      index
    );
    headers?.correlationId &&
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
        headers?.correlationId
      );

    const providerTierDesignation =
      practitioner?.recommendationDetails?.benefits?.providerTierDesignation;
    const llmStatus = practitioner?.recommendationDetails?.quality?.llmStatus;
    const updatedSearchRadius = displayVirtualVisit
      ? autoIncrementRadiusArr?.[autoIncrementRadiusArr?.length - 1]
      : searchRadius;
    const providerDetailsRoute = getProviderDetailsRouteUrl(
      chipStore,
      providerId,
      locationId,
      showDirection,
      isTieredProvider,
      sectionTypeDerived,
      choosePCP,
      isTieredPlan,
      acoProvider,
      practitioner?.llmCodes,
      updatedSearchRadius,
      isNameSearch || isNotSpecialitySearch,
      providerTierDesignation,
      llmStatus,
      enablePlanTypeSelectionInFilter ? networkId : undefined,
      dependentSeqNbr,
      recommendationPreferences?.suppressPremium,
      recommendationPreferences?.suppressCostEfficiency,
      undefined,
      practitioner?.uniqueIdentifier
    );

    setLastSelectedCardSessionStorage(
      ConstantsRoutes.PROVIDER_DETAILS.key,
      providerDetailsRoute
    );
    navigate(providerDetailsRoute);
  };

  const showDataDirection = () => {
    if (handleDirectionClick) {
      if (usedAt === Constants.MOBILE_LIST_VIEW) {
        setNavigateToDirections?.(true);
        setSelectedId(locationId || null);
      } else {
        setNavigateToDirections?.(true);
        handleDirectionClick();
      }
    } else {
      setNavigateToDirections?.(true);
      handleDetailsOnClick({ showDirection: true });
      setShowDirection(true);
    }
  };
  const handleOnHeadingBlur = () => {
    if (map?.current) {
      setResultIcon(
        map.current,
        +providerId,
        false,
        setHighlightId,
        providerId
      );
      updatePin?.(undefined);
    }
    setHighlightId({ providerId: '', from: usedAt });
  };

  const handleNavigationOnClick = () => {
    if (map?.current && setRouteEndCoords) {
      clearRoute(
        map.current,
        setRouteEndCoords,
        highlightId.providerId,
        setHighlightId
      );
      setSelectedId(null);
    }
    showDataDirection();
  };

  const onCardClick = () => {
    if (showBestMatchDetails) {
      handleAdobeSearchResultClickEvent('datacard click');
      setIsOpen(true);
    } else {
      handleDetailsOnClick({ showDirection: false });
      setShowDirection(false);
    }
  };
  const handleChoosePCPClick = (event) => {
    adobeSearchResultClickEvent({
      linkName: 'choose-pcp-button',
      linkPosition: getLinkPositionIndex(index),
      linkLocation: linkLocationName,
      customAttributesBlock: {
        ...(isSmartChoicePractitioner && {
          smartChoiceScore: `smart choice score: ${practitioner?.recommendationDetails?.totalRecommendationScore}`,
        }),
        correlationId: headers.correlationId,
        providerId: formatProviderId(
          appendCoverageTypes(practitioner, coverageType)
        ),
        providerType: convertProviderTypeToAdobeType(providerType),
      },
    });

    setAnalyticsState({
      originLinkNameForAnalytics: 'choose-pcp-button',
    });

    if (enablePCPParamsFromURL) {
      const pcpStoreValues = {
        choosePCPId: event.currentTarget.dataset.providerid,
        selectedProviderType: providerType,
      };
      setPCPSearchState(pcpStoreValues);

      const urlParams = convertObjectToUrlParams(chipStore, {
        ...pcpStoreValues,
        coverageType,
      });
      const choosePcpRoute =
        ConstantsRoutes.CHOOSE_PCP_LOCATION.path + urlParams;
      setLastSelectedCardSessionStorage(
        ConstantsRoutes.CHOOSE_PCP_LOCATION.key,
        choosePcpRoute
      );
      navigate(`${ConstantsRoutes.CHOOSE_PCP_LOCATION.path}${urlParams}`);
      return;
    }
    const pcpStoreValues = {
      choosePCPId: event.currentTarget.dataset.providerid,
      selectedProviderType: providerType,
      choosePCP: choosePCPFlag,
    };
    setPCPSearchState(pcpStoreValues);

    const urlParams = convertObjectToUrlParams(chipStore, {
      ...pcpStoreValues,
      value,
      coverageType,
    });

    if (isGroupedLocationDrawer) {
      const choosePcpRoute =
        ConstantsRoutes.CHOOSE_PCP_LOCATION.path + urlParams;
      setLastSelectedCardSessionStorage(
        ConstantsRoutes.CHOOSE_PCP_LOCATION.key,
        choosePcpRoute
      );
    }
    navigate(`${ConstantsRoutes.CHOOSE_PCP_LOCATION.path}${urlParams}`);
  };
  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressVirtualCare = shouldSuppressVirtualCare(
    isVirtualCareQuickCareSuppressed,
    coverageType,
    isVirtualCareUnetRailroadSuppressed
  );

  const isIFPOrganization = isIFPOrganizationCheck(
    providerType,
    currentMember?.isIFPMember
  );

  const displayVirtualVisit = shouldDisplayVirtualVisit(
    virtualCareIndicatorEnabled,
    suppressVirtualCare,
    practitioner?.address,
    isIFPOrganization,
    !!practitioner?.virtualCareOffered?.length,
    providerType
  );

  const imgSrc = practitioner?.imageLocation;

  const specialityTitleText = getSpecialityTitle(
    providerType,
    coverageType,
    providerSpeciality,
    t
  );
  const truncatedSpecialityTitle = truncateSpecialityTitle(
    isGroupedLocationDrawer,
    specialityTitleText,
    notXlScreen
  );

  const reciprocityId = getNetworkIdsForPES(
    currentMember,
    coverageType,
    featureFlags
  );

  const customPreferredPoliciesData: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();

  const customPreferredPoliciesWithLabel = customPreferredPoliciesData?.filter(
    (policy) => policy?.badgeLabel && policy?.icon
  );
  const eligibilities = useVirtualCareStore((state) => state.eligibilities);
  const { planToken } = getPlanSelectorVariables();

  const situsState = getSitusState(currentMember, storeLob);

  const selectedMember = getLoggedInMember();

  const customPreferredContent = getCustomPreferredPolicyData(
    customPreferredPoliciesWithLabel,
    currentMember?.policyNumber,
    reciprocityId,
    situsState
  );
  const isCustomPreferredPolicy = !!customPreferredContent;

  const customPreferredHeading =
    customPreferredContent?.badgeLabel ??
    Constants.PREFERRED_PROVIDER_BENEFITS.HEADING;

  const preferredIcon =
    customPreferredContent?.icon ??
    Constants.PREFERRED_PROVIDER_BENEFITS.GREEN_DIAMOND;

  const allConfigs = useLagoon('config')();
  const acoBenefitPlanCollection = allConfigs?.find(
    (row) => row.key === 'ACO_BENEFIT_COLLECTION'
  )?.value;

  const autoIncrementRadius = allConfigs?.find(
    (row) => row.key === 'AUTO_INCREMENT_SEARCH_RADIUS'
  )?.value;
  const suppressAddressAndMiles = shouldSuppressAddressAndMiles(
    enableVirtualCareEpic,
    isVirtualCareUnetRailroadSuppressed,
    virtualIndicator,
    value,
    eligibilities[planToken],
    allConfigs
  );
  const isNewMapViewDataCardDesign =
    !expandedProviderListView && enableMapViewHideOption;
  const autoIncrementRadiusArr = getAutoIncrementRadius(autoIncrementRadius);

  const providerTierDesignation =
    practitioner?.recommendationDetails?.benefits?.providerTierDesignation;
  const llmStatus = practitioner?.recommendationDetails?.quality?.llmStatus;
  const updatedSearchRadius = displayVirtualVisit
    ? autoIncrementRadiusArr?.[autoIncrementRadiusArr?.length - 1]
    : searchRadius;

  const providerDetailsPath = getProviderDetailsRouteUrl(
    chipStore,
    providerId,
    locationId,
    showDirection,
    isTieredProvider,
    sectionTypeDerived,
    choosePCP,
    isTieredPlan,
    acoProvider,
    practitioner?.llmCodes,
    updatedSearchRadius,
    isNameSearch || isNotSpecialitySearch,
    providerTierDesignation,
    llmStatus,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    practitioner?.uniqueIdentifier
  );

  const providerDetailsUrl = getProviderDetailsUrl(
    portalData,
    providerDetailsPath
  );

  const isAcoBenefitPlan = getAcoBenefitPlan(
    defaultStringToEmpty(
      getProductCodeForCoverageType(
        selectedMember,
        ReverseCoverageTypesCodes.MEDICAL
      )
    ),
    defaultStringToEmpty(getMarketType(selectedMember)),
    defaultStringToEmpty(selectedMember?.membershipCategory),
    defaultStringToEmpty(selectedMember?.population),
    acoBenefitPlanCollection
  );

  const coverageTypeDental = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );

  const isDentalCare = ReverseDentalCodes.includes(
    coverageTypeDental.coverageType
  );

  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoicePractitioner = smartChoiceEligibility.isEnabled;
  const smartChoicePriceCheck = getIsSmartChoicePriceCheck(
    isSmartChoicePractitioner,
    isTieredPlan,
    isAcoBenefitPlan,
    acoProvider,
    isCustomPreferredPolicy
  );

  const {
    isTier2Provider,
    isPremiumCarePhysician,
    isPreferredLab = false,
    isFreeStandingFacility = false,
    isOxPreferredLab,
    recommendationDetails,
  } = practitioner || {};

  const isTier1ProviderEligible = displayTier1Identifier(isTieredProvider);

  const isDisplayTier1Identifier = getTierProviderEligibility(
    currentMember,
    isTier1ProviderEligible
  );

  const isDisplayTier2Identifier = displayTier2Identifier(
    isTier2Provider,
    currentMember?.isIFPTieredPlan
  );

  const memberHasRPK = useStore(StoreKeys.TIER1_PLAN_FLAG);

  const suppressFacilityFlags = getSuppressFacilityFlags();

  const ddpCodeUser = getLoggedInMember()?.ddpCode;
  const ddpCode = practitioner?.ddpCode as FacilityDDPCode;
  const { isOXMember } = getIsOxfordAndUSP(
    currentMember,
    coverageType,
    featureFlags
  );

  const showBenefitsBadge = isShowPreferredFacilityBadge(
    suppressFacilityFlags,
    preferredFacilityFlag,
    ddpCode,
    ddpCodeUser,
    isPreferredLab,
    isOxPreferredLab,
    isOXMember,
    isFreeStandingFacility,
    oxfordPlnFlag
  );
  const showPremiumCare = displayPremiumCareIdentifier(
    !(suppressPremiumCare || suppressPremiumCareForNiceMembers),
    getPremiumCarePhysician(isPremiumCarePhysician),
    isDisplayTier1Identifier,
    isDisplayTier2Identifier,
    memberHasRPK,
    isCustomPreferredPolicy
  );

  const getFlexDirection = () => 'column';

  const getNetworkStatusIcon = () => {
    if (networkStatus) return '$success1';
    return '';
  };

  const networkDisplay = () => (
    <>
      <NetworkWrapper
        cssProps={{ enableMapViewHideOption }}
        data-testid="in-network-badge-container"
      >
        <IconSymbol
          color={getNetworkStatusIcon()}
          css={{
            aspectRatio: '1/1',
          }}
          data-auto-testid={`data-card-check-circle-icon-${sectionTypeDerived}-${providerId}`}
          data-testid={`data-card-check-circle-icon-${sectionTypeDerived}-${providerId}`}
          icon="check_circle"
          size={notXlScreen ? 16 : 20}
          variant="outlined"
        />
        <ResponsiveText
          cssProps={{ isGroupedLocationDrawer }}
          data-auto-testid={`data-card-network-${sectionTypeDerived}-${providerId}`}
          data-testid={`data-card-network-${sectionTypeDerived}-${providerId}`}
          fontWeight="$medium"
        >
          {t(inOrOutOfNetwork(networkStatus))}
        </ResponsiveText>
      </NetworkWrapper>
    </>
  );

  const avatarDisplay = () => (
    <ImageFlexContainer>
      <DataCardAvatarContainer>
        <AvatarDataCard
          cssProps={{ expandedProviderListView, isGroupedLocationDrawer }}
        >
          {ImageComponent({
            imgRef,
            providerType,
            imgSrc,
            bhIcon,
            sizeToken: '$dataCardIconSize',
            expandedProviderListView,
            isGroupedLocationDrawer,
            notXlScreen,
          })}
        </AvatarDataCard>

        <>
          <Flex
            css={GroupedPinSmartChoiceListViewAvatar}
            data-testid={`data-card-network-${sectionTypeDerived}-section`}
            direction="row"
            justify="center"
          >
            {isSmartChoicePractitioner &&
              enableMapViewHideOption &&
              (providerType?.toLowerCase() ===
                ProviderType.PRACTITIONER.toLowerCase() ||
                (providerType?.toLowerCase() ===
                  ProviderType.ORGANIZATION.toLowerCase() &&
                  value === CareCategories.FACILITIES)) &&
              parseInt(recommendationDetails?.totalRecommendationScore || '0') >
                0 && (
                <SmartChoiceScoreCard
                  expandedProviderListView={expandedProviderListView}
                  isDataCard
                  isGroupedLocationDrawer={true}
                  recommendationDetails={
                    recommendationDetails as RecommendationDetails
                  }
                />
              )}
          </Flex>
          <Flex
            css={{
              paddingTop: '0px',
              justifyContent: 'center',
            }}
          >
            {networkDisplay()}
          </Flex>
        </>
      </DataCardAvatarContainer>
    </ImageFlexContainer>
  );

  const nameAndBookmarkDisplay = () => (
    <Flex>
      <NameHeading
        data-auto-testid="data-card-heading-container"
        data-testid="data-card-heading-container"
        enableMapViewHideOption={enableMapViewHideOption}
        expandedProviderListView={expandedProviderListView}
        handleDetailsOnClick={handleDetailsOnClick}
        handleHoverOnCard={handleHoverOnCard}
        handleOnHeadingBlur={handleOnHeadingBlur}
        href={providerDetailsUrl}
        isGroupedLocationDrawer={true}
        isNewMapViewDataCardDesign={isNewMapViewDataCardDesign}
        name={name}
        popupContainer={popupContainer}
        providerId={providerId}
        sectionTypeDerived={sectionTypeDerived}
      />
    </Flex>
  );

  const dentalAndSpecialityDisplay = () => (
    <>
      {isDentalLocationNameEnable && isDentalCare && (
        <Tooltip content={practitioner?.address?.locationName}>
          <SnackRole
            css={{ fontWeight: 'bold' }}
            data-auto-testid="get-provider-location-name"
            data-testid="get-provider-location-name"
          >
            {practitioner?.address?.locationName}
          </SnackRole>
        </Tooltip>
      )}
      <Flex css={flexSpecialityAndBadgeStyle(badgeCardType)}>
        {' '}
        <Flex>
          {
            <Tooltip
              content={specialityTitleText}
              css={providerNameListViewToolTipStyles}
              position="top"
              positionOffset={8}
            >
              <ResponsiveTextSpeciality
                as="div"
                css={{
                  fontSize: notXlScreen ? '14px' : '18px',
                  color: '$gray8',
                  fontWeight: '400',
                }}
                data-auto-testid={`data-card-specialty-${sectionTypeDerived}`}
                data-testid={`data-card-specialty-${sectionTypeDerived}`}
                fontWeight="400"
                size="20.25px"
              >
                {truncatedSpecialityTitle}
              </ResponsiveTextSpeciality>
            </Tooltip>
          }
        </Flex>
      </Flex>
    </>
  );

  const virtualVisitDisplay = () => (
    <>
      {displayVirtualVisit && !suppressVirtualCare && (
        <Layout.Group css={VirtualVisitPatientStyleGroupedLocation}>
          <IconMaterial
            color="$gray8"
            data-auto-testid="videocam-icon"
            icon="videocam"
            size={notXlScreen ? 16 : 20}
            variant="outlined"
          />
          <Tooltip
            content={t('DETAILS_PAGE.OFFERS_VIRTUAL_CARE') || ''}
            css={virtualVistsToolTipStyles}
            position="top"
            positionOffset={8}
          >
            <VirtualVisitLabel
              as="div"
              css={{
                color: '$gray8',
                fontSize: notXlScreen ? '14px' : '16px',
                fontWeight: '400',
                marginLeft: '0px !important',
              }}
              data-auto-testid={`data-card-virtual-visits-${sectionTypeDerived}`}
              data-testid={`data-card-virtual-visits-${sectionTypeDerived}`}
            >
              {t('DETAILS_PAGE.OFFERS_VIRTUAL_CARE')}
            </VirtualVisitLabel>
          </Tooltip>
        </Layout.Group>
      )}
    </>
  );
  const ratingsDisplay = () => (
    <>
      {!suppressPatientReviews && (
        <Flex
          alignItems="center"
          css={{
            'abyss-flex-root': {
              cursor: shouldShowCursorOnRatings(healthGradeRating),
            },
          }}
          data-testid="data-card-clickable-reviews"
          onClick={ratingsOnClickHandler(
            healthGradeRating,
            handleDetailsOnClick,
            Constants.PATIENT_REVIEWS_SECTION
          )}
        >
          {sectionTypeDerived === Constants.RESULT_SECTION.PROVIDER && (
            <ProviderRating
              data-testid={`data-card-rating-${sectionTypeDerived}`}
              enableMapViewHideOption={enableMapViewHideOption}
              expandedProviderListView={expandedProviderListView}
              healthGradeRating={healthGradeRating}
              isGroupedLocationDrawer={true}
              popupContainer={popupContainer}
              providerId={providerId}
              sectionTypeDerived={sectionTypeDerived}
              size={valCheck(mobileScreen, 12, 13)}
              surveyCount={practitioner?.surveyCount}
              underline={false}
            />
          )}
        </Flex>
      )}
    </>
  );

  const milesAndAddressDisplay = () => (
    <div ref={addressMilesRef}>
      <Flex
        alignItems={'flex-start'}
        css={{
          paddingTop: smallScreen ? '0px' : '16px',
          flexWrap: 'wrap',
        }}
      >
        {!suppressAddressAndMiles && (
          <>
            <>
              <Button
                aria-labelledby={`LocName${providerId} LocAdrs${providerId}`}
                css={CardButtonGroupedLocationStyle}
                data-auto-testid="datacard-address"
                data-testid="datacard-address"
                onClick={() => {
                  handleAdobeSearchResultClickEvent('address');
                  handleNavigationOnClick();
                }}
                role="button"
                variant="tertiary"
              >
                {
                  <Address
                    address={address}
                    badgeCardType={badgeCardType}
                    enableMapViewHideOption={enableMapViewHideOption}
                    expandedProviderListView={expandedProviderListView}
                    id={providerId}
                    isCTADisplayLogic={ctaDisplayLogic}
                    isGroupedLocationDrawer={true}
                    sectionType={sectionTypeDerived}
                    showSmartChoice={showSmartChoice}
                  />
                }
              </Button>

              <VoiceOnlyText>
                {t('DIRECTIONS_FOR')} {name}
              </VoiceOnlyText>
            </>
            <div
              aria-labelledby={`LocMiles${providerId}`}
              data-auto-testid={`data-card-location-address-${address}-${providerId}`}
              data-testid={`data-card-location-address-${address}-${providerId}`}
            >
              <Miles
                addressMilesRef={addressMilesRef}
                distance={distance || ''}
                enableMapViewHideOption={enableMapViewHideOption}
                headers={headers}
                id={providerId}
                indexForAnalytics={getLinkPositionIndex(index)}
                isCTADisplayLogic={ctaDisplayLogic}
                isGroupedLocationDrawer={true}
                isNewMapViewDataCardDesign={isNewMapViewDataCardDesign}
                locationForAnalytics={linkLocationName}
                onClosePopup={() => setIsOpen(false)}
                onMilesClick={onCardClick}
                popupContainer={popupContainer}
                practitioner={practitioner}
                providerType={providerType}
                searchTerm={searchTerm}
                sectionType={sectionTypeDerived}
                selectedFilters={selectedFilters}
                showBestMatchDetails={showBestMatchDetails}
                showSmartChoice={showSmartChoice}
              />
            </div>
          </>
        )}
      </Flex>
    </div>
  );
  const groupedLocationBadgeCss = {
    fontSize: '16px',
    fontWeight: '600',
    '@media (max-width: 1248px)': {
      fontSize: '14px',
    },
  };

  const groupedLocationBadgeIconSize = () => (notXlScreen ? 16 : 20);

  const badgesDisplay = () => (
    <>
      {isPlatinumProviderIdentifierEnabled && isPlatinumProvider && (
        <Flex
          alignItems="center"
          data-testid="platinum-recognition-badge-container"
          direction="row"
        >
          <Text
            css={{
              color: '#323333',
              fontWeight: '600',
              marginLeft: '4px',
              ...groupedLocationBadgeCss,
            }}
          >
            {t('PROVIDER_DETAILS.ABOUT_TAB.PLATINUM_PROVIDER')}
          </Text>
        </Flex>
      )}
      {
        // PREMIUM CARE BADGE
        isPremiumCareDisplayEligible(
          !(suppressPremiumCare || suppressPremiumCareForNiceMembers),
          true
        ) && showPremiumCare ? (
          <Flex alignItems="center" direction="row">
            <IconSymbol
              color="$primary1"
              icon="favorite"
              size={groupedLocationBadgeIconSize()}
            />
            <IconSymbol
              color="$primary1"
              icon="favorite"
              size={groupedLocationBadgeIconSize()}
            />
            <Text css={{ marginLeft: '4px', ...groupedLocationBadgeCss }}>
              {t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_PHYSICIAN_TAG')}
            </Text>
          </Flex>
        ) : null
      }

      {
        // TIER 1
        isDisplayTier1Identifier ? (
          <Flex alignItems="center" direction="row">
            <PSXIcon
              icon="Group 204976975"
              size={groupedLocationBadgeIconSize()}
            />
            <Text css={{ marginLeft: '4px', ...groupedLocationBadgeCss }}>
              {t('Tier 1')}
            </Text>
          </Flex>
        ) : null
      }

      {
        // TIER 2
        isDisplayTier2Identifier ? (
          <Flex alignItems="center" direction="row">
            <PSXIcon icon="tier_two" size={groupedLocationBadgeIconSize()} />{' '}
            <Text css={{ marginLeft: '4px', ...groupedLocationBadgeCss }}>
              {t('Tier 2')}
            </Text>
          </Flex>
        ) : null
      }

      {
        // PREFERRED PROVIDER / - GREEN DIAMOND
        valCheck(
          isCustomPreferredPolicy && isGreenDiamondIndicator,
          <Flex alignItems="center" direction="row">
            <PSXIcon
              icon={preferredIcon}
              size={groupedLocationBadgeIconSize()}
            />
            <Text css={{ marginLeft: '4px', ...groupedLocationBadgeCss }}>
              {customPreferredHeading}
            </Text>
          </Flex>,
          null
        )
      }

      {!isGreenDiamondIndicator &&
      !!displayPreferredFacilityOnCards(
        showPremiumCare,
        showBenefitsBadge,
        isDisplayTier1Identifier,
        isDisplayTier2Identifier
      ) ? (
        <Flex alignItems="center" direction="row">
          <Text
            css={{
              marginLeft: '4px',
              fontWeight: '600',
              color: '#323333',
            }}
          >
            {t('PREFERRED_FACILITY_BENEFITS.TAG')}
          </Text>
        </Flex>
      ) : null}
    </>
  );

  const costDisplay = () => (
    <>
      {/* COST INDICATOR */}
      {enableCost &&
        portalShowCost &&
        practitioner?.costIndicator &&
        !suppressCostEstimates &&
        smartChoicePriceCheck && (
          <Flex css={costIndicatorStyles}>
            <CostIndicatorPopover
              callFrom="datacard"
              costIndicator={practitioner.costIndicator}
              enableMapViewHideOption={enableMapViewHideOption}
              headers={headers}
              indexForAnalytics={getLinkPositionIndex(index)}
              isGroupedLocationDrawer={true}
              locationForAnalytics={linkLocationName}
              practitioner={practitioner}
              providerType={providerType}
              searchTerm={searchTerm}
              selectedFilters={selectedFilters}
            />
          </Flex>
        )}
    </>
  );

  const callAndScheduleDisplay = () => (
    <>
      <ChoosePCP
        choosePCP={choosePCP}
        doShowDocASAP={doShowDocASAP}
        docasapPracticeId={docasapPracticeId}
        enableMapViewHideOption={enableMapViewHideOption}
        expandedProviderListView={expandedProviderListView}
        getLinkPositionIndex={getLinkPositionIndex}
        handleAdobeSearchResultClickEvent={handleAdobeSearchResultClickEvent}
        handleChoosePCPClick={handleChoosePCPClick}
        handleNavigationOnClick={handleNavigationOnClick}
        headers={headers}
        index={index}
        isCTADisplayLogic={ctaDisplayLogic}
        isGroupedLocationDrawer={true}
        isLimitedAccessToProvider={
          isLimitedAccessToProvider &&
          practitioner?.hospitalBasedProviderIndicator
        }
        isNavigatingMaestro={isNavigatingMaestro}
        isSchedulingWithFacilityEnabled={
          isSchedulingWithFacilityEnabled &&
          coverageType === ReverseCoverageTypesCodes['MEDICAL']
        }
        isTieredDataCard={isTieredProvider}
        linkLocationName={linkLocationName}
        locationId={locationId}
        name={name}
        npi={npi}
        practitioner={practitioner}
        primaryPhone={primaryPhone}
        providerId={providerId}
        providerType={providerType}
        searchTerm={searchTerm}
        sectionTypeDerived={sectionTypeDerived}
        selectedFilters={selectedFilters}
        showDirection={showDirection}
        showPcpBadge={showPcpBadge}
      />
    </>
  );

  if (smallScreen) {
    return (
      <GroupedLocationDrawerCardContainer
        className={''}
        data-auto-testid="generic-data-card"
        data-testid="generic-data-card"
        id={`data-card-for-map-${providerId}`}
        ref={cardRef}
      >
        <Flex
          css={{ height: 'auto', width: '100%', flexWrap: 'nowrap' }}
          direction="row"
        >
          <Card.Section
            css={{
              padding: '24px  16px 24px 16px',
              '@screen < $sm': { padding: '12px  12px 24px 12px' },
            }}
            data-testid="data-card-details-avatar-section"
          >
            {avatarDisplay()}
          </Card.Section>
          <Card.Section
            css={{
              borderTop: 'none !important',
              padding: '4px',
              width: smallScreen ? '210px' : '286px',
            }}
          >
            <Flex direction="column">
              {pcpBadgeDisplay()}
              <Flex css={{ gap: '0px' }}>
                <Flex>{nameAndBookmarkDisplay()}</Flex>
                <Flex>{dentalAndSpecialityDisplay()}</Flex>
              </Flex>
              <div style={{ paddingTop: '8px' }} />
              <Flex css={{ gap: '4px' }}>
                <Flex>{badgesDisplay()}</Flex>
                <Flex>{ratingsDisplay()}</Flex>
                <Flex>{costDisplay()}</Flex>
                <Flex>{virtualVisitDisplay()}</Flex>
              </Flex>
              <Flex css={{ paddingTop: '8px' }}>
                {milesAndAddressDisplay()}
              </Flex>
            </Flex>
          </Card.Section>
        </Flex>
        <Card.Section
          css={{
            padding: '4px',
            width: smallScreen ? '325px' : '',
          }}
        >
          {callAndScheduleDisplay()}
        </Card.Section>
      </GroupedLocationDrawerCardContainer>
    );
  }

  return (
    <GroupedLocationDrawerCardContainer
      className={''}
      data-auto-testid="generic-data-card"
      data-testid="generic-data-card"
      id={`data-card-for-map-${providerId}`}
      ref={cardRef}
    >
      <Card.Section
        css={{
          padding: '24px  16px 16px 16px',
          '@screen < $sm': { padding: '12px  12px 24px 12px' },
        }}
        data-testid="data-card-details-primary-section"
      >
        <Flex justify="space-between">
          {avatarDisplay()}
          <Flex
            css={groupedLocationDrawerNameAndBadgeStyle()}
            direction="column"
          >
            {pcpBadgeDisplay()}
            {nameAndBookmarkDisplay()}
            <Flex
              css={{ padding: '0px 0 0px $sm' }}
              direction={getFlexDirection()}
              justify="space-between"
            >
              {dentalAndSpecialityDisplay()}
              {!notXlScreen && virtualVisitDisplay()}
              {<>{milesAndAddressDisplay()}</>}
            </Flex>
          </Flex>

          <Flex
            alignItems="flex-end"
            css={{
              display: 'flex',
              width: '20%',
              paddingRight: '',
              gap: notXlScreen ? '4px' : '8px',
              flex: '1 0 0',
              paddingTop: '',
            }}
            direction="column"
          >
            {badgesDisplay()}

            {/* PATIENTS RATING STARS */}
            {ratingsDisplay()}
            {/* COST INDICATOR */}
            {costDisplay()}
            {/* VIRTUAL VISIT INDICATOR IN MED-SM SCREEN*/}
            {notXlScreen && virtualVisitDisplay()}
          </Flex>
        </Flex>
      </Card.Section>
      {callAndScheduleDisplay()}
    </GroupedLocationDrawerCardContainer>
  );
};
